import { createSlice, original } from '@reduxjs/toolkit'

import itemFilterOptionsJson from '../../data/config/itemFilterOptions.json'
import kindNoneSuPanelSupplementaryJson from '../../data/panelSupplementary/kindNonePanelSupplementary.json'
import simpleArticlePanelSupplementaryJson from '../../data/panelSupplementary/simpleArticlePanelSupplementary.json'
import { getDmPropsForChosenValues } from '../../helpers/getDmPropsForChosenValues'
import { isLfcItem } from '../../helpers/isLfcItem'
import { isOld } from '../../helpers/isOld'
import { groupByKey } from '../../helpers/groupByKey'
import { fetchDataForItems_aggregated, fetchPrices_aggregated } from '../requests/thunks'
import { getItemTypeForDmItem } from '../../helpers/getItemTypeForDynaMakerItem'
import * as DynamakerContract from '../../integration/DynamakerContract'
import { getNextDivergentProperties } from './getNextDivergentProperties'

const _handleFilterChange = (
  state: ItemsState,
  {
    itemType,
    updated,
  }: {
    itemType: keyof ItemsState['filterOptions']
    updated: Record<string, any>
  }
): void => {
  state.filterOptions = {
    ...state.filterOptions,
    [itemType]: {
      ...state.filterOptions[itemType],
      ...Object.fromEntries(
        Object.entries(updated).map(([key, value]) => [
          key,
          // @ts-expect-error // TODO
          state.filterOptions[itemType][key].map((option) => ({
            ...option,
            isActive: option.value === value,
          })),
        ])
      ),
    },
  }
}

interface ERPItem {
  Title?: string
  TitleLong?: string
  PricingInfo?: any
  Attributes?: any
  Sku: string
  contextId?: string
}

interface LFCItem {
  configuratorContextId?: string
  contextId?: string
  _lastFetched?: any
  dimensions: Array<{
    dimensionCode: string
  }>
}

export type DivergentPropertyOrigin = 'filterProperties' | 'dmProps'
type DivergentPropertyValue = {
  origin: DivergentPropertyOrigin
  values: Record<string, DynamakerContract.ArticleQty['erpItemNumber'][]>
}
export type DivergentProperties = Record<string, DivergentPropertyValue>
export type DivergentPropertiesPerItemGroup = Record<DynamakerContract.ItemGroup, DivergentProperties>

interface ItemsState {
  erpItems: ERPItem[]
  lfcItems: LFCItem[]
  renderItems: Record<DynamakerContract.ItemGroup, any[]>
  filterOptions: typeof itemFilterOptionsJson
  withDivergentProperties: DivergentPropertiesPerItemGroup
  _latestPendingRequestId_fetchPrices_aggregated: string
}

const initialState: ItemsState = {
  // array of all available articles from Scensum
  erpItems: [],
  // array of all available configurators from LFC with presets of config start data (initial chosenValues and configResult)
  lfcItems: [], // [ { _lastFetched: (timestamp), configuratorContextId: "",  dimensions: [], constraintsRules: [], presets: [] }, ... ]
  // obj with arrays of all avaiable and possible items to render, incl. all variants of presets for each mallis configurator
  renderItems: {
    doors: [],
    doubleDoors: [],
    facadeSections: [],
    garageDoors: [],
    slidingDoors: [],
    windows: [],
  },
  filterOptions: itemFilterOptionsJson,
  // arrays of filter property keys where current configResult bom has items where filter property values doesn't match
  withDivergentProperties: {
    doors: {},
    doubleDoors: {},
    facadeSections: {},
    garageDoors: {},
    slidingDoors: {},
    windows: {},
  },
  _latestPendingRequestId_fetchPrices_aggregated: '',
}

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    handleFilterChange(state, action) {
      const { itemType, updated } = action.payload

      _handleFilterChange(state, { itemType, updated })
    },
    handleFilterChangeForSelectedItem(state, action) {
      const selectedItem = action.payload

      const itemType = getItemTypeForDmItem(selectedItem.item as DynamakerContract.ItemType)
      if (!itemType) {
        console.error('missing itemType')
        return
      }

      const renderItems = state.renderItems[itemType]

      if (!renderItems || renderItems.length === 0) {
        console.error(
          `handleFilterChangeForSelectedItem reducer: Unable to find renderItems in Redux store for DM selectedItem: ${JSON.stringify(
            selectedItem
          )}`
        )
        return
      }

      const renderItem = renderItems.find((item) => item.erpItemNumber === selectedItem.erpItemNumber)

      if (!renderItem) {
        console.error(
          `handleFilterChangeForSelectedItem reducer: No matching renderItem was found in Redux store for DM selectedItem: ${JSON.stringify(
            selectedItem
          )}`
        )
        return
      }

      const { _filterProperties } = renderItem

      if (!_filterProperties) {
        console.warn(
          `handleFilterChangeForSelectedItem reducer: property _filterProperties is missing for renderItem in Redux store: ${JSON.stringify(
            renderItem
          )}`
        )
        return
      }

      _handleFilterChange(state, {
        itemType,
        updated: Object.fromEntries(
          Object.entries(_filterProperties).filter(
            ([property]: [string, any]) => original(state as any)?.filterOptions[itemType]?.[property]
          )
        ),
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataForItems_aggregated.fulfilled, (state, action) => {
        /*
        TODO: Add check for latestPendingRequestId, equivalent to check done in configResultSlice for fetchPrices_aggregated
        This is only a theoretical need, currently, since mergedItemsFromDm are never updated during app lifecycle
        Also add the check in requestsSlice
      */
        const {
          erpItems: erpItemsPayload,
          lfcItems: lfcItemsPayload,
          mergedItems: mergedItemsFromDm, // doors and windows from DM, merged
        } = action.payload

        const erpItems = state.erpItems.concat(
          erpItemsPayload.filter(
            (payloadItem: any) => !state.erpItems.some((stateItem) => stateItem.Sku === payloadItem.Sku)
          )
        )

        const _lastFetched = Date.now()

        const lfcItems = state.lfcItems
          .concat(
            lfcItemsPayload.filter(
              (payloadItem: any) => !state.lfcItems.some((stateItem) => stateItem.contextId === payloadItem.contextId)
            )
          )
          .flatMap((lfcItem) => {
            if (!lfcItem._lastFetched) {
              return {
                _lastFetched,
                ...lfcItem,
              }
            }

            if (isOld(lfcItem._lastFetched)) {
              const payloadItem = lfcItemsPayload.find(
                (payloadItem: any) => payloadItem.configuratorContextId === lfcItem.configuratorContextId
              )

              if (payloadItem) {
                return {
                  _lastFetched,
                  ...payloadItem,
                }
              }

              // (keep old items for now)
              return lfcItem
            }

            // already in state and not considered old
            return lfcItem
          })
          .map((lfcItem) => ({
            ...lfcItem,
            dimensions: lfcItem.dimensions.flatMap((dimension: any) =>
              dimension.dimensionCode === 'dorColor' || dimension.dimensionCode === 'wdwColor'
                ? [
                    {
                      ...dimension,
                      values: dimension.values.filter((value: any) => value.factoryValueCode !== 'OPTIONAL_COLOR'),
                    },
                  ]
                : [dimension]
            ),
          }))

        const warnedSortOrderIds = new Set<string>()
        const uniqueItems = new Set()
        const itemsToRender = mergedItemsFromDm
          .flatMap((dmItem: DynamakerContract.Item) => {
            if (isLfcItem(dmItem as any)) {
              // TODO
              const erpItem = erpItems.find((erpItem) => erpItem.Sku === dmItem.erpItemNumber)

              // TODO lägg tillbaka när bugg rättats i Scensum
              // if (!erpItem.contextId) {
              //   console.warn(
              //     `contextId is missing for erpItem with Sku: ${erpItem.Sku}`
              //   )
              //   return [];
              // }

              const lfcItem = lfcItems.find(
                // TODO lägg tillbaka när bugg rättats i Scensum
                // (lfcItem) => lfcItem.configuratorContextId === erpItem.contextId
                (lfcItem) => lfcItem.configuratorContextId === erpItem?.Sku
              )

              if (!lfcItem) {
                console.warn(`No lfcItem matching erpItem.contextId ${erpItem?.contextId}`)
                return []
              }

              return lfcItem.presets.map((preset: any) => ({
                _internalId: `${lfcItem.configuratorContextId}--${preset.namedConfigCode}`,
                _editorial: preset.presetPanelSupplementary.editorial,
                _filterProperties: preset.presetPanelSupplementary.filterProperties,
                _sortOrder: preset.presetPanelSupplementary.sortOrder,
                item: dmItem.item,
                erpItemNumber: dmItem.erpItemNumber,
                props: {
                  ...dmItem.props,
                  ...getDmPropsForChosenValues(
                    preset.chosenValues,
                    lfcItem.dimensions,
                    lfcItem.configuratorContextId, // for error logging only
                    preset // for error logging only
                  ),
                  configData: {
                    _lastFetched: lfcItem._lastFetched,
                    contextId: lfcItem.configuratorContextId,
                    namedConfigCode: preset.namedConfigCode,
                    chosenValues: preset.chosenValues,
                    configResult: preset.configResult,
                  },
                },
              }))
            }

            const dmItemProps = dmItem.props as any
            // Module without item (empty)
            if (dmItem.erpItemNumber === null && dmItem.props.kind === 'None') {
              const _internalId = `${dmItem.item}--${dmItem.props.kind}${
                dmItemProps.moduleHeight
                  ? `--${dmItemProps.moduleHeight}` // added when item is "window"
                  : ''
              }${
                dmItemProps.openingWidth
                  ? `--${dmItemProps.openingWidth}` // added when item is "garage-door" | "sliding-door" | "double-door" | "facade-section"
                  : ''
              }${
                dmItemProps.openingHeight
                  ? `--${dmItemProps.openingHeight}` // added when item is "garage-door" | "sliding-door" | "double-door" | "facade-section"
                  : ''
              }`

              return [
                {
                  _internalId,
                  // @ts-expect-error TODO: fix type
                  _editorial: kindNoneSuPanelSupplementaryJson[_internalId]
                    ? // @ts-expect-error TODO: fix type
                      kindNoneSuPanelSupplementaryJson[_internalId].editorial
                    : null,
                  _filterProperties: {},
                  // @ts-expect-error TODO: fix type
                  _sortOrder: kindNoneSuPanelSupplementaryJson[_internalId]
                    ? // @ts-expect-error TODO: fix type
                      kindNoneSuPanelSupplementaryJson[_internalId].sortOrder
                    : 0,
                  ...dmItem,
                },
              ]
            }

            // Without an article from Scensum the card has neither title, image nor price to show
            if (dmItem.erpItemNumber !== null && !erpItems.some((erpItem) => erpItem.Sku === dmItem.erpItemNumber))
              return []

            // Simple article
            return [
              {
                _internalId: dmItem.erpItemNumber,
                // @ts-expect-error TODO: fix type
                _editorial: simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber]
                  ? // @ts-expect-error TODO: fix type
                    simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber].editorial
                  : null,
                // @ts-expect-error TODO: fix type
                _filterProperties: simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber]
                  ? // @ts-expect-error TODO: fix type
                    simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber].filterProperties
                  : {},
                // @ts-expect-error TODO: fix type
                _sortOrder: simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber]
                  ? // @ts-expect-error TODO: fix type
                    simpleArticlePanelSupplementaryJson[dmItem.erpItemNumber].sortOrder
                  : 0,
                ...dmItem,
              },
            ]
          })
          .filter((item: { _internalId: string }) => {
            if (uniqueItems.has(item._internalId)) return false
            uniqueItems.add(item._internalId)
            return true
          })
          .sort((a: any, b: any) => {
            for (const item of [a, b]) {
              if (typeof item._sortOrder !== 'number' && !warnedSortOrderIds.has(item._internalId)) {
                warnedSortOrderIds.add(item._internalId)
                console.warn(
                  `Item with _internalId "${item._internalId}" is missing a numeric _sortOrder (got ${JSON.stringify(item._sortOrder)}). Falling back to 0.`
                )
              }
            }
            return (a._sortOrder ?? 0) - (b._sortOrder ?? 0)
          })

        const groupedRenderItems = groupByKey(itemsToRender, 'item')

        state.erpItems = erpItems
        state.lfcItems = lfcItems
        state.renderItems = {
          doors: groupedRenderItems['door'] ?? [],
          windows: groupedRenderItems['window'] ?? [],
          garageDoors: groupedRenderItems['garage-door'] ?? [],
          doubleDoors: groupedRenderItems['double-door'] ?? [],
          slidingDoors: groupedRenderItems['sliding-door'] ?? [],
          facadeSections: groupedRenderItems['facade-section'] ?? [],
        }
      })
      .addCase(fetchPrices_aggregated.pending, (state, action) => {
        state._latestPendingRequestId_fetchPrices_aggregated = action.meta.requestId
      })
      .addCase(fetchPrices_aggregated.fulfilled, (state, action) => {
        if (state._latestPendingRequestId_fetchPrices_aggregated === action.meta.requestId) {
          const {
            configResult: { bom },
          } = action.payload

          state.withDivergentProperties = getNextDivergentProperties(
            bom,
            original(state)?.renderItems as Record<DynamakerContract.ItemGroup, any[]>,
            original(state)?.lfcItems as any[]
          )
        }
      })
      .addCase(fetchPrices_aggregated.rejected, (state, action) => {
        if (state._latestPendingRequestId_fetchPrices_aggregated === action.meta.requestId) {
          const {
            configResult: { bom },
          } = action.payload as { configResult: Pick<DynamakerContract.ConfigResult, 'bom'> }

          state.withDivergentProperties = getNextDivergentProperties(
            bom,
            original(state)?.renderItems as Record<DynamakerContract.ItemGroup, any[]>,
            original(state)?.lfcItems as any[]
          )
        }
      })
  },
})

export const { handleFilterChange, handleFilterChangeForSelectedItem } = itemsSlice.actions

export default itemsSlice.reducer
